import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient } from './queryClient';
import { teamAssistantKeys } from '../hooks/useTeamAssistantQueries';
import {
  TEAM_ASSISTANT_PERSIST_MAX_AGE,
  shouldPersistTeamAssistantQuery,
  teamAssistantPersistBuster,
  teamAssistantQueryPersister,
} from './teamAssistantQueryPersist';

/**
 * Persists Team Assistant messages + sync-meta to IndexedDB (per logged-in user).
 * After hydrate, refetches sync-meta so shared staff chat stays in sync across devices.
 */
export function TeamAssistantQueryPersistProvider({ children }) {
  const userId = useSelector((state) => state.user.currentUser?.id);
  const buster = teamAssistantPersistBuster(userId);

  const persistOptions = useMemo(
    () => ({
      persister: teamAssistantQueryPersister,
      maxAge: TEAM_ASSISTANT_PERSIST_MAX_AGE,
      buster,
      dehydrateOptions: {
        shouldDehydrateQuery: shouldPersistTeamAssistantQuery,
      },
    }),
    [buster]
  );

  const onSuccess = useCallback(() => {
    queryClient.refetchQueries({ queryKey: teamAssistantKeys.syncMeta, type: 'active' });
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={onSuccess}
    >
      {children}
    </PersistQueryClientProvider>
  );
}

export default TeamAssistantQueryPersistProvider;
